import { flooringAreaValueNamePresenter } from '@kaiizen/kai-presenters';
import {
  getEffectiveScanValueAsNumber,
  getGroupFlooringAreaValueNames,
  type ScanGroupOptimized,
} from '@kaiizen/kai-scan-template-system';

/**
 * How every measurement readout prints a number: rounded to the whole unit
 * with thousands separators, and an em dash where there is nothing to show.
 */
export const formatWholeMeasurement = (value: number | undefined | null) =>
  value == null || Number.isNaN(value)
    ? '—'
    : Math.round(value).toLocaleString();

/**
 * Room dimensions for a group, new-system-first: prefer the RoomFloorPlan's
 * width/length/height, falling back to the legacy roomWidth/roomLength/
 * roomHeight scan values so groups predating the floor-plan model still show.
 */
export const getGroupDimensions = (group: ScanGroupOptimized) => {
  const plan = group.roomFloorPlan;
  if (plan) return { width: plan.width, length: plan.length, height: plan.height };
  return {
    width: getEffectiveScanValueAsNumber(group.values?.roomWidth),
    length: getEffectiveScanValueAsNumber(group.values?.roomLength),
    height: getEffectiveScanValueAsNumber(group.values?.roomHeight),
  };
};

/**
 * A group's area for one flooring-area value name. Zero is "none of this
 * material here", not a measurement, so it reads the same as unset — the
 * floor plan zeroes every unpainted type on save.
 */
export const getGroupFlooringArea = (group: ScanGroupOptimized, valueName: string) => getEffectiveScanValueAsNumber(group.values?.[valueName]) || undefined;

/**
 * Every flooring-area value name the given groups' templates define, ordered
 * by display label. One shared order keeps a material in the same place — and
 * on the same hatch — everywhere it appears.
 */
export const getFlooringAreaValueNames = (groups: readonly ScanGroupOptimized[]): string[] =>
  [...new Set(groups.flatMap(group => getGroupFlooringAreaValueNames(group.template)))]
    .sort((a, b) => flooringAreaValueNamePresenter(a).localeCompare(flooringAreaValueNamePresenter(b)));

/** Per-flooring-type totals across the groups; undefined where no group carries the value. */
export const getFlooringAreaTotals = (
  groups: readonly ScanGroupOptimized[],
  valueNames: readonly string[],
): Record<string, number | undefined> =>
  Object.fromEntries(valueNames.map(valueName => {
    const areas = groups.flatMap(group => getGroupFlooringArea(group, valueName) ?? []);
    return [valueName, areas.length ? areas.reduce((sum, area) => sum + area, 0) : undefined];
  }));

/** The group's flooring materials that carry area, largest first. */
export const getGroupFlooringAreas = (
  group: ScanGroupOptimized,
  valueNames: readonly string[],
): { valueName: string; area: number }[] =>
  valueNames
    .flatMap(valueName => {
      const area = getGroupFlooringArea(group, valueName);
      return area == null ? [] : [{ valueName, area }];
    })
    .sort((a, b) => b.area - a.area);
